import React from 'react';
import { ShoppingCart, Send, BarChart3, FileTextIcon, ShieldCheckIcon, CameraIcon, MonitorIcon, ScanFaceIcon, BotIcon, EyeIcon } from 'lucide-react';
export function HowItWorks() {
  const steps = [{
    icon: <ShoppingCart className="h-8 w-8 sm:h-10 sm:w-10 text-primary" data-id="element-187" />,
    title: 'Buy your recruitment tests',
    description: 'Choose how many you need (1, 10, or 25+).'
  }, {
    icon: <Send className="h-8 w-8 sm:h-10 sm:w-10 text-primary" data-id="element-188" />,
    title: 'Invite candidates',
    description: 'Send secure test links to applicants.'
  }, {
    icon: <BarChart3 className="h-8 w-8 sm:h-10 sm:w-10 text-primary" data-id="element-189" />,
    title: 'Compare results',
    description: 'View scores, skill breakdowns, and detailed insights instantly.'
  }];
  return <section id="how-it-works" className="py-12 md:py-20 bg-gray-50" data-id="element-190">
      <div className="container mx-auto px-4 max-w-7xl" data-id="element-191">
        <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3 sm:mb-4" data-id="element-192">
          How Do Laravel Recruitment Tests Work?
        </h2>
        <p className="text-center text-gray-600 mb-8 sm:mb-12 px-4" data-id="element-193">
          Three simple steps to evaluate Laravel developer skills and find the
          right talent for your team.
        </p>
        <div className="flex flex-col md:flex-row gap-6 sm:gap-8 justify-between relative" data-id="element-194">
          {steps.map((step, index) => <div key={index} className="flex-1 bg-white p-6 sm:p-8 rounded-xl shadow-sm border border-gray-100" data-id="element-195">
              <div className="flex flex-col items-center text-center" data-id="element-196">
                <div className="relative mb-4 sm:mb-5" data-id="element-197">
                  <div className="bg-gray-50 p-3 sm:p-4 rounded-full" data-id="element-198">
                    {step.icon}
                  </div>
                  <span className="absolute -top-1 -right-1 w-6 h-6 bg-primary text-white text-xs font-bold rounded-full flex items-center justify-center shadow-sm" data-id="element-199">
                    {index + 1}
                  </span>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3" data-id="element-200">
                  {step.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-600" data-id="element-201">
                  {step.description}
                </p>
              </div>
            </div>)}
          <div className="hidden md:block absolute top-1/3 left-0 right-0 h-0.5 bg-gray-200 -z-10" data-id="element-202"></div>
        </div>

        {/* Recruitment Test Details */}
        <div className="mt-12 sm:mt-16 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-203">
          <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-200" data-id="element-204">
            {/* Test Format */}
            <div className="p-6 sm:p-8" data-id="element-205">
              <div className="flex items-center mb-5" data-id="element-206">
                <div className="bg-gray-50 p-2.5 rounded-lg mr-3" data-id="element-207">
                  <FileTextIcon className="w-5 h-5 text-primary" data-id="element-208" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900" data-id="element-209">
                  Test Format
                </h3>
              </div>
              <div className="space-y-4" data-id="element-210">
                <div className="flex justify-between items-center py-3 border-b border-gray-100" data-id="element-211">
                  <span className="text-sm sm:text-base text-gray-600" data-id="element-212">
                    Questions
                  </span>
                  <span className="text-sm sm:text-base font-semibold text-gray-900" data-id="element-213">
                    45 MCQs
                  </span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100" data-id="element-214">
                  <span className="text-sm sm:text-base text-gray-600" data-id="element-215">
                    Format
                  </span>
                  <span className="text-sm sm:text-base font-semibold text-gray-900" data-id="element-216">
                    Code snippets + 1 correct answer
                  </span>
                </div>
                <div className="flex justify-between items-center py-3" data-id="element-217">
                  <span className="text-sm sm:text-base text-gray-600" data-id="element-218">
                    Pass threshold
                  </span>
                  <span className="text-sm sm:text-base font-semibold text-primary" data-id="element-219">
                    75% correct
                  </span>
                </div>
              </div>
            </div>

            {/* Proctoring & Security */}
            <div className="p-6 sm:p-8" data-id="element-220">
              <div className="flex items-center mb-5" data-id="element-221">
                <div className="bg-gray-50 p-2.5 rounded-lg mr-3" data-id="element-222">
                  <ShieldCheckIcon className="w-5 h-5 text-primary" data-id="element-223" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900" data-id="element-224">
                  Fully Proctored
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-3" data-id="element-225">
                <div className="flex items-center text-sm sm:text-base text-gray-700" data-id="element-226">
                  <CameraIcon className="w-4 h-4 text-primary mr-2 flex-shrink-0" data-id="element-227" />
                  <span data-id="element-228">Camera & sound recording</span>
                </div>
                <div className="flex items-center text-sm sm:text-base text-gray-700" data-id="element-229">
                  <MonitorIcon className="w-4 h-4 text-primary mr-2 flex-shrink-0" data-id="element-230" />
                  <span data-id="element-231">Screen lock enabled</span>
                </div>
                <div className="flex items-center text-sm sm:text-base text-gray-700" data-id="element-232">
                  <EyeIcon className="w-4 h-4 text-primary mr-2 flex-shrink-0" data-id="element-233" />
                  <span data-id="element-234">No second screen</span>
                </div>
                <div className="flex items-center text-sm sm:text-base text-gray-700" data-id="element-235">
                  <MonitorIcon className="w-4 h-4 text-primary mr-2 flex-shrink-0" data-id="element-236" />
                  <span data-id="element-237">No leaving the screen</span>
                </div>
                <div className="flex items-center text-sm sm:text-base text-gray-700" data-id="element-238">
                  <BotIcon className="w-4 h-4 text-primary mr-2 flex-shrink-0" data-id="element-239" />
                  <span data-id="element-240">Anti-cheat AI analysis</span>
                </div>
                <div className="flex items-center text-sm sm:text-base text-gray-700" data-id="element-241">
                  <EyeIcon className="w-4 h-4 text-primary mr-2 flex-shrink-0" data-id="element-242" />
                  <span data-id="element-243">Human review</span>
                </div>
                <div className="flex items-center text-sm sm:text-base text-gray-700 col-span-2" data-id="element-244">
                  <ScanFaceIcon className="w-4 h-4 text-primary mr-2 flex-shrink-0" data-id="element-245" />
                  <span data-id="element-246">ID identification required</span>
                </div>
              </div>
              <div className="mt-5 pt-5 border-t border-gray-100 flex items-center gap-3" data-id="element-247">
                <span className="text-xs text-gray-500 font-medium" data-id="element-248">
                  Powered by
                </span>
                <img src="https://cdn.magicpatterns.com/uploads/hatj81h6QCVKcPGkbUZgT3/image.png" alt="Pruefster" className="h-6 sm:h-7 object-contain" data-id="element-249" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}